import { FORECAST_LAYOUTS_CONFIG, CANNED_ACTIVITIES_CONFIG } from '../../../config';
import { ForecastLayout, ForecastLayoutTarget, ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from './types';
import { MeteogramWidget } from './widgets/MeteogramWidget';
import { SoundingWidget } from './widgets/SoundingWidget';
import { LightningMapWidget } from './widgets/LightningMapWidget';
import { MiniMapWidget } from './widgets/MiniMapWidget';
import { SunIntensityWidget } from './widgets/SunIntensityWidget';
import { WindWidget } from './widgets/WindWidget';
import { AirQualityWidget } from './widgets/AirQualityWidget';
import { SolarAlpenglowWidget } from './widgets/SolarAlpenglowWidget';
import { DarkSkiesAstroWidget } from './widgets/DarkSkiesAstroWidget';
import { DarkSkiesCardWidget } from './widgets/DarkSkiesCardWidget';
import { AuroraCardWidget } from './widgets/AuroraCardWidget';
import { ObservedRadarWidget } from './widgets/ObservedRadarWidget';
import { ObservedSurfaceWidget } from './widgets/ObservedSurfaceWidget';
import { Glance4x1Widget } from './widgets/Glance4x1Widget';
import { OpticsDewRiskWidget } from './widgets/OpticsDewRiskWidget';
import { StatGridWidget } from './widgets/StatGridWidget';
import { HorizonSelectorWidget } from './widgets/HorizonSelectorWidget';
import { DailyForecastListWidget } from './widgets/DailyForecastListWidget';
import { DailyForecastStripWidget } from './widgets/DailyForecastStripWidget';
import { DailyPrecipWidget } from './widgets/DailyPrecipWidget';
import { DailySnowWidget } from './widgets/DailySnowWidget';
import { ElevationTempWidget } from './widgets/ElevationTempWidget';
import { CloudCeilingWidget } from './widgets/CloudCeilingWidget';
import { RecentWeatherWidget } from './widgets/RecentWeatherWidget';
import { PersonaChipsWidget } from './widgets/PersonaChipsWidget';
import { Glance2x2Widget } from './widgets/Glance2x2Widget';
import { StreamflowWidget } from './widgets/StreamflowWidget';
import { KpiGridWidget } from './widgets/KpiGridWidget';
import { SolarEphemerisWidget } from './widgets/SolarEphemerisWidget';
import { DarkSkyCalendarWidget } from './widgets/DarkSkyCalendarWidget';
import { NearbyWildfiresWidget } from './widgets/NearbyWildfiresWidget';
import { TropicalStormsWidget } from './widgets/TropicalStormsWidget';
import { ObservedSnowWidget } from './widgets/ObservedSnowWidget';

const WIDGET_REGISTRY: Record<string, WidgetRenderer> = {
  // Horizon selector
  horizon_selector: new HorizonSelectorWidget(),
  'horizon-selector': new HorizonSelectorWidget(),

  // Daily forecast
  daily_forecast_list: new DailyForecastListWidget(),
  'daily-forecast-list': new DailyForecastListWidget(),
  daily_forecast_card: new DailyForecastListWidget(),
  'daily-forecast-card': new DailyForecastListWidget(),
  daily_forecast_strip: new DailyForecastStripWidget(),
  'daily-forecast-strip': new DailyForecastStripWidget(),

  // Daily precipitation forecast
  daily_precip_card: new DailyPrecipWidget(),
  'daily-precip-card': new DailyPrecipWidget(),
  daily_precip: new DailyPrecipWidget(),
  'daily-precip': new DailyPrecipWidget(),
  daily_precipitation_card: new DailyPrecipWidget(),
  'daily-precipitation-card': new DailyPrecipWidget(),

  // Daily snowfall forecast
  daily_snow_card: new DailySnowWidget(),
  'daily-snow-card': new DailySnowWidget(),
  daily_snow: new DailySnowWidget(),
  'daily-snow': new DailySnowWidget(),
  daily_snowfall_card: new DailySnowWidget(),
  'daily-snowfall-card': new DailySnowWidget(),

  // Meteograms
  meteogram_card: new MeteogramWidget(),
  'meteogram-card': new MeteogramWidget(),

  // Elevation temp lapse
  elevation_temp_card: new ElevationTempWidget(),
  'elevation-temp-card': new ElevationTempWidget(),

  // Cloud ceilings
  cloud_ceiling_card: new CloudCeilingWidget(),
  'cloud-ceiling-card': new CloudCeilingWidget(),

  // Recent weather / Recent rainfall
  recent_rainfall: new RecentWeatherWidget(),
  'recent-rainfall': new RecentWeatherWidget(),
  recent_weather_2x3: new RecentWeatherWidget(),
  'recent-weather-2x3': new RecentWeatherWidget(),
  recent_weather: new RecentWeatherWidget(),
  'recent-weather': new RecentWeatherWidget(),

  // Activity / Persona chips
  persona_chips: new PersonaChipsWidget(),
  'persona-chips': new PersonaChipsWidget(),

  // Golden Window & Alpenglow
  golden_window_card: new SolarAlpenglowWidget(),
  'golden-window-card': new SolarAlpenglowWidget(),
  solar_alpenglow_card: new SolarAlpenglowWidget(),
  'solar-alpenglow-card': new SolarAlpenglowWidget(),

  // Dark Skies Stargazing & Trend
  darkskies_card: new DarkSkiesCardWidget(),
  'darkskies-card': new DarkSkiesCardWidget(),
  darkskies_aurora_card: new DarkSkiesCardWidget(),
  'darkskies-aurora-card': new DarkSkiesCardWidget(),

  // Aurora & Space Weather
  aurora_card: new AuroraCardWidget(),
  'aurora-card': new AuroraCardWidget(),
  aurora_map_card: new AuroraCardWidget(),

  // Legacy astronomical card fallback
  astronomical_card: new DarkSkiesAstroWidget(),
  'astronomical-card': new DarkSkiesAstroWidget(),

  // Observed MRMS Radar & Precip
  observed_radar_card: new ObservedRadarWidget(),
  'observed-radar-card': new ObservedRadarWidget(),

  // Observed RTMA Surface Mesonet
  observed_surface_card: new ObservedSurfaceWidget(),
  'observed-surface-card': new ObservedSurfaceWidget(),

  // Stat grid
  stat_grid: new StatGridWidget(),
  'stat-grid': new StatGridWidget(),

  // Quick Glances (2x2, 2x3, and 4x1)
  glance_2x2: new Glance2x2Widget(),
  'glance-2x2': new Glance2x2Widget(),
  glance_2x3: new Glance2x2Widget(),
  'glance-2x3': new Glance2x2Widget(),
  weather_glance: new Glance2x2Widget(),
  'weather-glance': new Glance2x2Widget(),
  glance_4x1: new Glance4x1Widget(),
  'glance-4x1': new Glance4x1Widget(),

  // Streamflow
  streamflow_card: new StreamflowWidget(),
  'streamflow-card': new StreamflowWidget(),

  // SNOTEL Snowpack & Mountain Stations
  observed_snow_card: new ObservedSnowWidget(),
  'observed-snow-card': new ObservedSnowWidget(),
  snotel_snow_card: new ObservedSnowWidget(),
  'snotel-snow-card': new ObservedSnowWidget(),

  // KPI grid
  kpi_grid: new KpiGridWidget(),
  'kpi-grid': new KpiGridWidget(),

  // Ephemeris & Calendar
  solar_ephemeris: new SolarEphemerisWidget(),
  'solar-ephemeris': new SolarEphemerisWidget(),
  solar_lunar_calendar: new SolarEphemerisWidget(),
  'solar-lunar-calendar': new SolarEphemerisWidget(),
  darksky_calendar: new DarkSkyCalendarWidget(),
  'darksky-calendar': new DarkSkyCalendarWidget(),
  darkskies_calendar: new DarkSkyCalendarWidget(),
  'darkskies-calendar': new DarkSkyCalendarWidget(),

  // Legacy cards
  sounding_card: new SoundingWidget(),
  'sounding-card': new SoundingWidget(),
  lightning_map_card: new LightningMapWidget(),
  mini_map_card: new MiniMapWidget(),
  sun_intensity_card: new SunIntensityWidget(),
  wind_card: new WindWidget(),
  air_quality_card: new AirQualityWidget(),
  'air-quality-card': new AirQualityWidget(),
  air_quality_chart: new AirQualityWidget(),
  'air-quality-chart': new AirQualityWidget(),
  optics_dew_risk_card: new OpticsDewRiskWidget(),

  // Severe Hazards: Nearby Wildfires
  nearby_wildfires_card: new NearbyWildfiresWidget(),
  'nearby-wildfires-card': new NearbyWildfiresWidget(),
  wildfires_card: new NearbyWildfiresWidget(),
  'wildfires-card': new NearbyWildfiresWidget(),

  // Severe Hazards: Hurricanes & Tropical Storms
  tropical_cyclones_card: new TropicalStormsWidget(),
  'tropical-cyclones-card': new TropicalStormsWidget(),
  tropical_storms_card: new TropicalStormsWidget(),
  'tropical-storms-card': new TropicalStormsWidget(),
  hurricanes_card: new TropicalStormsWidget(),
  'hurricanes-card': new TropicalStormsWidget(),
};

export function getWidgetRenderer(type: string): WidgetRenderer | undefined {
  if (!type) return undefined;
  return WIDGET_REGISTRY[type] || 
         WIDGET_REGISTRY[type.replace(/-/g, '_')] || 
         WIDGET_REGISTRY[type.replace(/_/g, '-')];
}

const DEFAULT_FALLBACK_LAYOUT: ForecastLayout = {
  id: 'default',
  name: 'Default Balanced Forecast',
  columns: 12,
  widgets: [
    { id: 'horizon_selector', type: 'horizon_selector', title: 'Forecast Horizon', col_span: 12 },
    { id: 'temp_precip_meteo', type: 'meteogram_card', title: 'Temperature & Precipitation', col_span: 6 },
    { id: 'lightning_hazard', type: 'lightning_map_card', title: 'Daily Lightning Hazard & Strike Timing', col_span: 6 },
    { id: 'sounding_inversion', type: 'sounding_card', title: 'Atmospheric Sounding & Inversion Profile', col_span: 6 },
    { id: 'sun_intensity', type: 'sun_intensity_card', title: 'Sun Intensity & Solar Flux', col_span: 6 },
    { id: 'air_sky_quality', type: 'air_quality_card', title: 'Air & Sky Quality', col_span: 6 },
    { id: 'solar_alpenglow', type: 'solar_alpenglow_card', title: 'Sunrise, Sunset and Golden Hour Outlooks', col_span: 6 },
    { id: 'wind_profile', type: 'wind_card', title: 'Wind Speed & Direction', col_span: 6 },
    { id: 'astronomical_events', type: 'astronomical_card', title: 'Astronomical Events & Night Sky', col_span: 6 }
  ]
};

export function getLayoutForPersona(personaId: string, isMobile?: boolean): ForecastLayoutTarget {
  let rawLayout: ForecastLayout | null = null;

  // 'advanced' (All Map Layers) is a map catalog persona; render standard forecast layout
  const targetId = (personaId === 'advanced' || personaId === 'all_map_layers') ? 'standard_forecast' : personaId;

  // 1. Check canned persona's bundled layout
  const personaConfig = CANNED_ACTIVITIES_CONFIG[targetId];
  if (personaConfig && (personaConfig as any).forecastLayout) {
    rawLayout = (personaConfig as any).forecastLayout;
  } else if (FORECAST_LAYOUTS_CONFIG[targetId]) {
    rawLayout = FORECAST_LAYOUTS_CONFIG[targetId];
  } else if (FORECAST_LAYOUTS_CONFIG['default']) {
    rawLayout = FORECAST_LAYOUTS_CONFIG['default'];
  } else {
    rawLayout = DEFAULT_FALLBACK_LAYOUT;
  }

  const checkMobile = isMobile !== undefined ? isMobile : (typeof window !== 'undefined' && window.innerWidth <= 768);
  if (checkMobile && rawLayout && rawLayout.mobile && Array.isArray(rawLayout.mobile.widgets)) {
    return rawLayout.mobile;
  }
  if (rawLayout && rawLayout.web && Array.isArray(rawLayout.web.widgets)) {
    return rawLayout.web;
  }
  return {
    columns: rawLayout?.columns || 12,
    widgets: rawLayout?.widgets || []
  };
}

const WIDGET_DEFAULT_DESCRIPTIONS: Record<string, string> = {
  'glance-2x2': 'Real-time and near-real-time surface weather summary combining the nearest reporting observation station with point-specific temperature, dew point, relative humidity, directional wind compass, 50-mile lightning scan, and nearest AQI station.',
  'glance-2x3': 'Real-time and near-real-time surface weather summary combining the nearest reporting observation station with point-specific temperature, dew point, relative humidity, directional wind compass, 50-mile lightning scan, and nearest AQI station.',
  'glance-4x1': 'Compact 4-interval hourly weather strip summarizing near-term temperature, sky condition, wind speed, and precipitation trends.',
  'recent-weather-2x3': 'Observed 24-hour, 48-hour, and 72-hour liquid precipitation and snowfall accumulation totals derived from multi-sensor radar and gauge-corrected analysis (MRMS, CMORPH2, and ECMWF).',
  'recent-rainfall': 'Observed 24-hour, 48-hour, and 72-hour liquid precipitation and snowfall accumulation totals derived from multi-sensor radar and gauge-corrected analysis (MRMS, CMORPH2, and ECMWF).',
  'daily-forecast-list': 'Multi-day daily weather outlook displaying daily high/low temperature spans, sky condition icons, precipitation probability, and peak wind vectors. Tap or click any day row to expand 3-hour interval breakdowns.',
  'daily-forecast-card': 'Multi-day daily weather outlook displaying daily high/low temperature spans, sky condition icons, precipitation probability, and peak wind vectors. Tap or click any day row to expand 3-hour interval breakdowns.',
  'daily-forecast-strip': 'Horizontal multi-day forecast strip summarizing daily sky conditions, high/low temperatures, precipitation chances, and winds.',
  'daily-precip-card': 'Day-by-day liquid precipitation accumulation forecast (inches/mm) and daily probability of measurable rain, including a storm-total summary banner and peak wettest day highlight.',
  'daily-snow-card': 'Day-by-day snowfall accumulation forecast (inches/cm) and daily snow probability, highlighting total storm snowfall and the deepest powder day across the forecast horizon.',
  'elevation-temp-card': 'Hourly vertical temperature profile plotting simultaneous temperature curves at Summit/Peak, Mid-Mountain, and Valley/Base elevations alongside the 32°F (0°C) freezing level line to reveal thermal inversions and alpine lapse rates.',
  'cloud-ceiling-card': 'Vertical cross-section of cloud base (ceiling) and cloud top altitudes (ft MSL) relative to local mountain terrain elevation. Shows when summits will be socked-in inside the cloud deck versus above a valley cloud inversion (\'sea of clouds\').',
  'golden-window-card': 'Photographic light-quality forecast grading upcoming Sunrise, Sunset, Golden Hour, and Alpine Alpenglow sessions (0–100%) by analyzing multi-tier cloud geometry, sub-horizon ray paths, atmospheric aerosols, and local horizon terrain.',
  'solar-alpenglow-card': 'Photographic light-quality forecast grading upcoming Sunrise, Sunset, Golden Hour, and Alpine Alpenglow sessions (0–100%) by analyzing multi-tier cloud geometry, sub-horizon ray paths, atmospheric aerosols, and local horizon terrain.',
  'solar-lunar-calendar': '10-day astronomical ephemeris table and visual timeline detailing exact Sunrise, Sunset, Golden Hour, Blue Hour, Moonrise, Moonset, Lunar Phase, and % Lunar Illumination.',
  'solar-ephemeris': '10-day astronomical ephemeris table and visual timeline detailing exact Sunrise, Sunset, Golden Hour, Blue Hour, Moonrise, Moonset, Lunar Phase, and % Lunar Illumination.',
  'darkskies-card': 'Hourly nighttime stargazing quality forecast (0–100%) combining the site\'s baseline Bortle light-pollution class with cloud cover, lunar illumination, wildfire smoke, humidity, and atmospheric seeing.',
  'darkskies-aurora-card': 'Night-sky transparency and stargazing outlook combining the composite Dark Skies score with planetary geomagnetic Kp-index trends and aurora visibility potential.',
  'darksky-calendar': 'Multi-night stargazing planning calendar showing each night\'s peak Dark Skies score, total cloud-free dark hours, astronomical twilight window, and lunar interference curve.',
  'darkskies-calendar': 'Multi-night stargazing planning calendar showing each night\'s peak Dark Skies score, total cloud-free dark hours, astronomical twilight window, and lunar interference curve.',
  'aurora-card': 'Space weather and geomagnetic storm dashboard tracking the planetary Kp-index forecast, solar wind speed and Bz magnetic orientation, and localized probability of Aurora Borealis visibility.',
  'sounding-card': 'Vertical thermodynamic profile of temperature, dew point, freezing level, thermal inversions, and winds aloft from the surface up through the troposphere.',
  'lightning-map-card': 'Hourly convective thunderstorm and cloud-to-ground lightning hazard timeline showing peak diurnal strike probability windows.',
  'wind-card': 'Hourly 10-meter sustained wind speed, peak wind gusts, and compass wind direction trend for assessing exposure, water chop, and boundary-layer turbulence.',
  'sun-intensity-card': 'Hourly downwelling shortwave solar irradiance (W/m²) and UV exposure curve accounting for solar elevation angle and cloud attenuation.',
  'air-quality-card': 'Hourly Air Quality Index (AQI) and surface PM2.5 wildfire smoke concentration forecast for assessing respiratory health and atmospheric haze.',
  'air-quality-chart': 'Hourly Air Quality Index (AQI) and surface PM2.5 wildfire smoke concentration forecast for assessing respiratory health and atmospheric haze.',
  'astronomical-card': 'Nighttime astronomical summary highlighting twilight phases, moonrise/moonset timing, lunar illumination percentage, and dark-sky observing windows.',
  'observed-snow-card': 'Live telemetry from the nearest high-elevation SNOTEL and snow-pillow stations showing total snow depth, Snow Water Equivalent (SWE), 24h/72h new snowfall, and multi-day snowpack trends.',
  'snotel-snow-card': 'Live telemetry from the nearest high-elevation SNOTEL and snow-pillow stations showing total snow depth, Snow Water Equivalent (SWE), 24h/72h new snowfall, and multi-day snowpack trends.',
  'streamflow-card': 'Live river discharge (cfs) and stage height telemetry from nearby stream gauges compared against historical median flow percentiles.',
  'observed-radar-card': 'Live Multi-Radar Multi-Sensor (MRMS) reflectivity and recent precipitation accumulation summary at the forecast location.',
  'observed-surface-card': 'Hourly Real-Time Mesoscale Analysis (RTMA) ground-truth surface temperature, dew point, and wind observation.',
  'kpi-grid': 'At-a-glance environmental threshold indicators evaluating recent rainfall, relative humidity, surface temperature, and wind speed against optimal field activity criteria.',
  'stat-grid': 'Compact metric tile grid highlighting key environmental variables and real-time ground/canopy indicators for rapid field assessment.',
  'nearby-wildfires-card': 'Live proximity monitor scanning NIFC, CWFIS, and satellite thermal detections for active wildfires within range, showing distance, compass bearing, burn acreage, and containment status.',
  'wildfires-card': 'Live proximity monitor scanning NIFC, CWFIS, and satellite thermal detections for active wildfires within range, showing distance, compass bearing, burn acreage, and containment status.',
  'tropical-cyclones-card': 'Active NHC and JTWC tropical cyclone tracker displaying nearby tropical depressions, storms, and hurricanes with forecast intensity, track distance, arrival timing, and wind radii.',
  'tropical-storms-card': 'Active NHC and JTWC tropical cyclone tracker displaying nearby tropical depressions, storms, and hurricanes with forecast intensity, track distance, arrival timing, and wind radii.',
  'optics-dew-risk-card': 'Hourly optical condensation risk forecast (0–100) evaluating surface radiative cooling and dew point depression to warn when camera lenses and telescopes may fog.'
};

export function resolveWidgetDescription(w: ForecastWidgetConfig): string {
  const custom = (w.description || w.options?.description || '').trim();
  if (custom) return custom;

  const normType = (w.type || '').replace(/_/g, '-');
  if (normType === 'meteogram-card') {
    const series = Array.isArray(w.options?.series) ? w.options!.series : [];
    if (series.length > 0) {
      const labels = series
        .map((s: any) => s?.label || s?.key?.replace(/_/g, ' '))
        .filter(Boolean);
      if (labels.length > 0) {
        const joined = labels.length === 1
          ? labels[0]
          : `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
        return `Hourly multi-product meteogram (${w.title || 'Meteogram'}) plotting ${joined} across the selected forecast horizon. Hover or tap along the timeline to inspect exact hourly values and compare weather models.`;
      }
    }
    return 'Hourly multi-series meteogram plotting temperature and precipitation trends across the active forecast horizon.';
  }

  return WIDGET_DEFAULT_DESCRIPTIONS[normType] || '';
}

function attachTooltipToWidgetDom(colEl: HTMLElement, w: ForecastWidgetConfig, desc: string): void {
  const cardEl = (colEl.querySelector('.metric-card') as HTMLElement) || (colEl.firstElementChild as HTMLElement);
  if (!cardEl) return;
  if (cardEl.querySelector('.forecast-widget-info-wrap')) return;

  cardEl.style.position = cardEl.style.position || 'relative';

  const wrap = document.createElement('span');
  wrap.className = 'forecast-widget-info-wrap';
  wrap.style.cssText = 'position: relative; display: inline-flex; align-items: center; margin-left: 6px; vertical-align: middle; z-index: 30;';

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'forecast-widget-info-btn';
  btn.title = desc;
  btn.setAttribute('aria-label', `About ${w.title || 'this widget'}`);
  btn.textContent = 'ℹ️';
  btn.style.cssText = [
    'background: rgba(56, 189, 248, 0.12)',
    'border: 1px solid rgba(56, 189, 248, 0.35)',
    'border-radius: 999px',
    'width: 20px',
    'height: 20px',
    'padding: 0',
    'display: inline-flex',
    'align-items: center',
    'justify-content: center',
    'font-size: 11px',
    'line-height: 1',
    'cursor: pointer',
    'color: #38bdf8',
    'transition: all 0.15s ease',
    'flex-shrink: 0'
  ].join('; ');

  const pop = document.createElement('div');
  pop.className = 'forecast-widget-tooltip-popover';
  pop.style.cssText = [
    'display: none',
    'position: absolute',
    'top: calc(100% + 6px)',
    'right: 0',
    'left: auto',
    'width: min(320px, 78vw)',
    'background: rgba(15, 23, 42, 0.97)',
    'border: 1px solid rgba(56, 189, 248, 0.45)',
    'border-radius: 10px',
    'padding: 10px 12px',
    'box-shadow: 0 12px 28px rgba(0, 0, 0, 0.65)',
    'color: #e2e8f0',
    'font-size: 12px',
    'font-weight: 400',
    'line-height: 1.45',
    'text-transform: none',
    'letter-spacing: normal',
    'z-index: 999',
    'pointer-events: auto'
  ].join('; ');

  const safeTitle = (w.title || 'Widget Overview').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const safeDesc = desc.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  pop.innerHTML = `
    <div style="display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 4px; padding-bottom: 4px; border-bottom: 1px solid rgba(255,255,255,0.08);">
      <span style="font-size: 11.5px; font-weight: 700; color: #38bdf8;">ℹ️ ${safeTitle}</span>
    </div>
    <div style="color: #cbd5e1; font-size: 11.5px; line-height: 1.45;">${safeDesc}</div>
  `;

  let pinnedOpen = false;
  const adjustPopoverPosition = () => {
    pop.style.right = '0';
    pop.style.left = 'auto';
    const rect = pop.getBoundingClientRect();
    if (rect.left < 16) {
      pop.style.right = 'auto';
      pop.style.left = '0';
    }
  };

  btn.addEventListener('mouseenter', () => {
    btn.style.background = 'rgba(56, 189, 248, 0.25)';
    pop.style.display = 'block';
    adjustPopoverPosition();
  });

  btn.addEventListener('mouseleave', () => {
    if (!pinnedOpen) {
      btn.style.background = 'rgba(56, 189, 248, 0.12)';
      pop.style.display = 'none';
    }
  });

  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    e.preventDefault();
    pinnedOpen = !pinnedOpen;
    if (pinnedOpen) {
      document.querySelectorAll('.forecast-widget-tooltip-popover').forEach(el => {
        if (el !== pop) (el as HTMLElement).style.display = 'none';
      });
      pop.style.display = 'block';
      btn.style.background = 'rgba(56, 189, 248, 0.28)';
      adjustPopoverPosition();
    } else {
      pop.style.display = 'none';
      btn.style.background = 'rgba(56, 189, 248, 0.12)';
    }
  });

  document.addEventListener('click', (ev) => {
    if (pinnedOpen && !wrap.contains(ev.target as Node)) {
      pinnedOpen = false;
      pop.style.display = 'none';
      btn.style.background = 'rgba(56, 189, 248, 0.12)';
    }
  });

  wrap.appendChild(btn);
  wrap.appendChild(pop);

  // Always position the info button at the top-right of the widget header
  const headerEl = cardEl.firstElementChild as HTMLElement | null;
  if (headerEl) {
    const tag = headerEl.tagName.toUpperCase();
    const inlineStyle = (headerEl.getAttribute('style') || '').toLowerCase();
    if (tag === 'H3' || tag === 'H4') {
      headerEl.style.display = 'flex';
      headerEl.style.justifyContent = 'space-between';
      headerEl.style.alignItems = 'center';
      headerEl.style.width = '100%';
      wrap.style.marginLeft = 'auto';
      headerEl.appendChild(wrap);
      return;
    }
    if (inlineStyle.includes('display: flex') || inlineStyle.includes('display:flex')) {
      if (headerEl.children.length >= 2) {
        const lastChild = headerEl.lastElementChild as HTMLElement | null;
        const lastStyle = (lastChild?.getAttribute('style') || '').toLowerCase();
        if (
          lastChild &&
          (lastStyle.includes('display: flex') ||
            lastStyle.includes('display:flex') ||
            lastStyle.includes('display: inline-flex') ||
            lastStyle.includes('display:inline-flex'))
        ) {
          lastChild.appendChild(wrap);
        } else if (lastChild) {
          const rightGroup = document.createElement('div');
          rightGroup.style.cssText = 'display: inline-flex; align-items: center; gap: 6px; margin-left: auto;';
          headerEl.replaceChild(rightGroup, lastChild);
          rightGroup.appendChild(lastChild);
          rightGroup.appendChild(wrap);
        } else {
          headerEl.appendChild(wrap);
        }
      } else {
        headerEl.style.justifyContent = 'space-between';
        headerEl.style.alignItems = 'center';
        wrap.style.marginLeft = 'auto';
        headerEl.appendChild(wrap);
      }
      return;
    }
  }

  wrap.style.position = 'absolute';
  wrap.style.top = '10px';
  wrap.style.right = '12px';
  cardEl.appendChild(wrap);
}

function injectWidgetInfoTooltip(w: ForecastWidgetConfig): void {
  if (typeof document === 'undefined') return;
  if (
    w.showInfoButton === false ||
    w.options?.showInfoButton === false ||
    w.options?.show_info_button === false
  ) {
    return;
  }
  const normType = (w.type || '').replace(/_/g, '-');
  if (['persona-chips', 'horizon-selector', 'app-bar', 'bottom-nav'].includes(normType)) {
    return;
  }
  const desc = resolveWidgetDescription(w);
  if (!desc) return;

  const colEl = document.querySelector(`.detailed-forecast-col[data-widget-id="${w.id}"]`) as HTMLElement | null;
  if (!colEl) return;

  attachTooltipToWidgetDom(colEl, w, desc);

  // Observe async widget content updates (e.g. wildfires, tropical storms, SNOTEL stations)
  const observer = new MutationObserver(() => {
    attachTooltipToWidgetDom(colEl, w, desc);
  });
  observer.observe(colEl, { childList: true, subtree: true });
}

export function renderLayoutHtml(layout: ForecastLayoutTarget, ctx: WidgetRenderContext): string {
  const widgetsHtml = (layout.widgets || []).map(w => {
    const renderer = getWidgetRenderer(w.type);
    if (!renderer) {
      console.warn(`[DetailedForecast] Unknown widget type '${w.type}', skipping.`);
      return '';
    }

    const colSpan = (w as any).colSpan || w.col_span || 6;
    const content = renderer.renderHtml(w, ctx);

    return `
      <div class="detailed-forecast-col" data-widget-id="${w.id}" style="grid-column: span ${colSpan}; display: flex; flex-direction: column; min-width: 0;">
        ${content}
      </div>
    `;
  }).join('');

  const gridCols = layout.columns || 12;
  return `
    <div style="display: grid; grid-template-columns: repeat(${gridCols}, 1fr); gap: 16px;">
      ${widgetsHtml}
    </div>
  `;
}

export function initLayoutWidgets(layout: ForecastLayoutTarget, ctx: WidgetRenderContext): void {
  (layout.widgets || []).forEach(w => {
    const renderer = getWidgetRenderer(w.type);
    if (renderer) {
      try {
        renderer.init(w, ctx);
        injectWidgetInfoTooltip(w);
      } catch (err) {
        console.warn(`[DetailedForecast] Failed to initialize widget '${w.id}' (${w.type}):`, err);
      }
    }
  });
}

export interface ForecastLoadRequirements {
  needsSolar?: boolean;
  needsStargazing?: boolean;
  needsMountains?: boolean;
  needsObserved?: boolean;
}

export function getRequirementsForPersona(personaId: string, isMobile?: boolean): ForecastLoadRequirements {
  const layout = getLayoutForPersona(personaId, isMobile);
  const widgetTypes = new Set((layout.widgets || []).map(w => w.type));

  const hasWidget = (...types: string[]) => types.some(t => 
    widgetTypes.has(t) || 
    widgetTypes.has(t.replace(/_/g, '-')) || 
    widgetTypes.has(t.replace(/-/g, '_'))
  );

  const needsSolar = hasWidget(
    'golden_window_card', 'solar_alpenglow_card',
    'solar_ephemeris', 'solar_lunar_calendar'
  );

  const needsStargazing = hasWidget(
    'darkskies_card', 'darkskies_aurora_card',
    'aurora_card', 'aurora_map_card',
    'astronomical_card', 'darksky_calendar',
    'darkskies_calendar'
  );

  const needsMountains = hasWidget(
    'elevation_temp_card', 'sounding_card', 'cloud_ceiling_card'
  );

  const needsObserved = hasWidget(
    'recent_weather_2x3', 'recent_weather',
    'observed_radar_card', 'observed_surface_card',
    'observed_snow_card', 'snotel_snow_card'
  );

  return { needsSolar, needsStargazing, needsMountains, needsObserved };
}
